import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Guardian } from '@dreryk/egabinet-patient-frontend-api';

import { createTranslatedText } from '@dreryk/react-common';
import { Button, ButtonVariant, Select } from '@dreryk/react-components';

import { entitlementStatementsTranslationKeys } from '../../../translations';

import { RepresentativeEntitlementStatementCreateFormController } from './RepresentativeEntitlementStatementCreateForm';

const StyledEntitlementStatementRepresentativeSelect = styled.div`
   display: grid;
   grid-template-columns: 368px 273px;
   column-gap: 36px;
   align-items: end;
`;
StyledEntitlementStatementRepresentativeSelect.displayName = 'StyledEntitlementStatementRepresentativeSelect';

type EntitlementStatementRepresentativeSelectRenderer = (renderProps: {
   existingRepresentative: boolean;
}) => JSX.Element | null;

type EntitlementStatementRepresentativeSelectProps = Pick<
   RepresentativeEntitlementStatementCreateFormController,
   'register' | 'setValue' | 'reset' | 'watch'
> & {
   readonly guardians: Guardian[];
   readonly children: EntitlementStatementRepresentativeSelectRenderer;
};

const getGuardianLabel = (guardian?: Guardian): string => {
   if (!guardian) {
      return '';
   }
   const externalIdentifierValue = guardian.externalIdentifier.accept<string>({
      visitPesel: (pesel) => pesel.asString(),
      visitPassport: (passport) => passport.getValue(),
      visitNationalIdentificationNumber: (nationalIdentificationNumber) => nationalIdentificationNumber.getValue(),
   });
   return `${guardian.firstName} ${guardian.lastName}, ${externalIdentifierValue}`;
};

export const EntitlementStatementRepresentativeSelect = (
   props: EntitlementStatementRepresentativeSelectProps
): JSX.Element | null => {
   const { register, setValue, watch, guardians } = props;
   const items = useMemo(() => guardians ?? [], [guardians]);
   const existingRepresentative = watch('existingRepresentative');
   const selected = Boolean(existingRepresentative);
   const controller = register('existingRepresentative', {
      required: selected,
   });

   useEffect(() => {
      const address = existingRepresentative?.address;
      setValue('representativeStreet', address?.findStreet()?.asString() ?? '', { shouldDirty: true });
      setValue('representativeHouse', address?.findBuilding()?.asString() ?? '', { shouldDirty: true });
      setValue('representativeFlat', address?.findFlat()?.asString() ?? null, { shouldDirty: true });
      setValue('representativeTown', address?.findTown()?.getName() ?? null, { shouldDirty: true });
      setValue('representativePostcode', address?.findPostcode() ?? null, { shouldDirty: true });
      setValue(
         'representativeIdentityDocument',
         { type: 'IdentityCard', seriesAndNumber: undefined, issuingCountry: undefined },
         { shouldDirty: true }
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [existingRepresentative?.id]);

   return (
      <>
         <StyledEntitlementStatementRepresentativeSelect>
            <Select<Guardian>
               testId={'representativeSelect'}
               triggerLabelExtractor={getGuardianLabel}
               label={entitlementStatementsTranslationKeys.createModal.guardian}
               placeholder={createTranslatedText('-')}
               controller={controller}
               disabled={isEmpty(items)}
            >
               <Select.List<Guardian> items={items} keyExtractor={'id'}>
                  {(guardian: Guardian) => (
                     <Select.Item
                        item={guardian}
                        testId={guardian.id}
                        label={createTranslatedText(getGuardianLabel(guardian))}
                     />
                  )}
               </Select.List>
            </Select>
            {selected ? (
               <Button
                  testId={'newRepresentative'}
                  variant={ButtonVariant.SECONDARY}
                  type='button'
                  onClick={() => {
                     setValue('existingRepresentative', null);
                  }}
                  fullLength
                  label={entitlementStatementsTranslationKeys.createModal.addNewGuardianData}
               />
            ) : null}
         </StyledEntitlementStatementRepresentativeSelect>
         {props.children({
            existingRepresentative: selected,
         })}
      </>
   );
};
